'use client'

import { StoreProvider } from 'app/StoreProvider'
import { usePathname } from 'next/navigation'
import { ReactNode, useState } from 'react'
import { useLayoutEffect } from 'react'

import { RootState } from 'store/rootReducer'

import { INITIAL_BREADCRUMBS_DOM_ID } from '@/app/layout/MainSiteLayout/breadcrumbs-initial-data'
import { LayoutBreadcrumbsSetterProvider } from '@/app/layout/MainSiteLayout/LayoutBreadcrumbsOverride'
import { MainSiteLayout } from '@/app/layout/MainSiteLayout/MainSiteLayout'

import { CardReviewRatingsProvider } from '@/features/product-reviews/model/card-review-ratings-provider'

import { TPage } from '@/shared/types/deprecated-types'

type Props = {
	children: ReactNode
	serverState: Partial<RootState>
	topBanner?: ReactNode
}

const getInitialBreadcrumbs = (): TPage[] | undefined => {
	if (typeof window === 'undefined') return undefined

	const jsonTag = window.document.getElementById(INITIAL_BREADCRUMBS_DOM_ID)
	if (!jsonTag?.textContent) return undefined

	try {
		const parsed = JSON.parse(jsonTag.textContent) as TPage[]

		return Array.isArray(parsed) ? parsed : undefined
	} catch {
		return undefined
	}
}

export const SiteLayoutClient = ({ children, serverState, topBanner }: Props) => {
	const pathname = usePathname()
	// Must start undefined on BOTH server and client so the first client render matches the server
	// HTML (no hydration mismatch). The product breadcrumbs are read from the DOM script and applied
	// in useLayoutEffect below — which runs before paint, so there's no visible flash.
	const [breadcrumbs, setBreadcrumbs] = useState<TPage[] | undefined>(undefined)

	useLayoutEffect(() => {
		const initial = getInitialBreadcrumbs()
		// Only set from DOM when the element actually exists (product pages).
		// For all other pages LayoutBreadcrumbsOverride sets breadcrumbs as a child effect
		// which fires before this parent effect — overwriting here with undefined would erase them.
		if (initial !== undefined) {
			setBreadcrumbs(initial)
		}
	}, [pathname])

	return (
		<StoreProvider serverState={serverState}>
			<CardReviewRatingsProvider>
				<LayoutBreadcrumbsSetterProvider setBreadcrumbs={setBreadcrumbs}>
					<MainSiteLayout breadcrumbs={breadcrumbs} topBanner={topBanner}>
						{children}
					</MainSiteLayout>
				</LayoutBreadcrumbsSetterProvider>
			</CardReviewRatingsProvider>
		</StoreProvider>
	)
}
